<script setup>
import { ref, computed, watch } from 'vue'
import { ACCESS_LABELS, itemRef, paidMappingChosen, plansEmptiedBy } from '@/Utils/siteAccess'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Alert from '@/components/Atoms/Alert.vue'
import SiteAccessSelect from './SiteAccessSelect.vue'

// "Who can watch" picker for one or many existing content items (Library
// bulk "Change access…" and the per-row quick action). Paid needs an explicit
// plan mapping — ticked `scope: items` plans or "Only all-access plans" — and
// the chosen `planIds` travel with the change; going Paid → Public/Login
// clears the mapping. The parent applies the change (runtime bulk endpoint or
// per-item PATCH) and calls `finish()` / `fail(message)`.
const props = defineProps({
	plans: {
		type: Array,
		default: () => [],
	},
	canMonetize: {
		type: Boolean,
		default: true,
	},
	stripe: {
		type: Object,
		default: null,
	},
})
const emit = defineEmits(['apply', 'create-plan', 'stripe-connected'])

const isVisible = ref(false)
const busy = ref(false)
const error = ref(null)
const items = ref([])
const access = ref('public')
const planIds = ref([])
const allAccessOnly = ref(false)

const refs = computed(() => items.value.map((i) => itemRef(i.id)))
const paidToday = computed(() => items.value.filter((i) => (i.access || 'public') === 'paid'))
const clearsMapping = computed(() => access.value !== 'paid' && paidToday.value.length > 0)
// Said before saving: plans that would be left unlocking nothing once these items leave them.
const emptiedPlans = computed(() => plansEmptiedBy(props.plans, refs.value, access.value === 'paid' ? planIds.value : []))
const emptiedTitles = computed(() => emptiedPlans.value.map((p) => `“${p.title}”`).join(' and '))
const ready = computed(() => paidMappingChosen(access.value, planIds.value, allAccessOnly.value))
const single = computed(() => items.value.length === 1)
const title = computed(() => (single.value ? 'Who can watch this item' : `Change access for ${items.value.length} items`))
const unchanged = computed(() => items.value.length > 0 && items.value.every((i) => (i.access || 'public') === access.value))

/**
 * @param {object[]} list                    content items to change
 * @param {{ access?: string, planIds?: string[] }} preset
 *   access: preselected level (defaults to the items' shared level, else public);
 *   planIds: `scope: items` plans unlocking the items today (preselected when paid).
 */
const show = (list, preset = {}) => {
	items.value = [...list]
	error.value = null
	busy.value = false
	const shared = list.length && list.every((i) => (i.access || 'public') === (list[0].access || 'public')) ? list[0].access || 'public' : null
	access.value = preset.access || shared || 'public'
	planIds.value = [...(preset.planIds || [])]
	// A paid item with no per-item plan today is unlocked by all-access plans only.
	allAccessOnly.value = access.value === 'paid' && shared === 'paid' && planIds.value.length === 0
	isVisible.value = true
}
const close = () => { isVisible.value = false }
const finish = () => { busy.value = false; isVisible.value = false }
const fail = (message) => { busy.value = false; error.value = message || 'Could not change access.' }
/** A plan created from "+ New plan" while this modal is open is ticked right away. */
const selectPlan = (product) => {
	if (!product?.public_id || product.scope !== 'items') return
	if (!planIds.value.includes(product.public_id)) planIds.value = [...planIds.value, product.public_id]
	allAccessOnly.value = false
}

watch(isVisible, (open) => { if (!open) { items.value = []; error.value = null; busy.value = false } })

const apply = () => {
	if (!ready.value || busy.value || !items.value.length) return
	busy.value = true
	error.value = null
	emit('apply', { items: items.value, access: access.value, planIds: access.value === 'paid' ? planIds.value : [] })
}

defineExpose({ show, close, finish, fail, selectPlan })
</script>

<template>
	<ModalBox v-model="isVisible" :title="title" size="sm" scroll-body wrapper-classes="lg:h-auto lg:max-h-[90vh]">
		<div class="flex flex-col gap-4 px-4 pb-4 sm:px-6 sm:pb-6" data-testid="access-change-modal">
			<Alert v-if="error" type="error" class="!mb-0">{{ error }}</Alert>

			<p v-if="single" class="mb-0 truncate text-sm text-surface-8" data-testid="access-change-item">{{ items[0].title }}</p>
			<p v-else class="mb-0 text-tiny text-surface-7" data-testid="access-change-items">
				{{ items.map((i) => i.title).slice(0, 3).join(', ') }}<template v-if="items.length > 3"> and {{ items.length - 3 }} more</template>
			</p>

			<SiteAccessSelect v-model="access" v-model:plan-ids="planIds" v-model:all-access-only="allAccessOnly"
				:products="plans" :refs="refs" :can-monetize="canMonetize" :stripe="stripe"
				@create-plan="emit('create-plan', $event)" @stripe-connected="emit('stripe-connected')" />

			<p v-if="clearsMapping" class="mb-0 rounded-md bg-warning/10 px-3 py-2 text-tiny text-warning" data-testid="access-clear-note">
				<template v-if="single">This item is paid today. Making it {{ ACCESS_LABELS[access] }} clears its plan mapping — it is removed from every plan that unlocked it.</template>
				<template v-else>{{ paidToday.length }} of these {{ items.length }} items {{ paidToday.length === 1 ? 'is' : 'are' }} paid today. Making them {{ ACCESS_LABELS[access] }} clears their plan mapping — they are removed from every plan that unlocked them.</template>
			</p>
			<p v-else-if="access === 'paid' && !single && planIds.length" class="mb-0 text-tiny text-surface-7" data-testid="access-replace-note">
				The ticked plans become the full list for every selected item — plans left unticked no longer unlock them.
			</p>
			<p v-if="emptiedPlans.length" class="mb-0 rounded-md bg-surface-3 px-3 py-2 text-tiny text-surface-8" data-testid="access-empties-note">
				{{ emptiedTitles }} would then unlock nothing — edit or archive {{ emptiedPlans.length === 1 ? 'it' : 'them' }} under Plans afterwards.
			</p>
		</div>

		<template #footer>
			<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
				<FormButton class="w-full justify-center sm:w-auto" :disabled="!ready || busy" :is-loading="busy" data-testid="access-change-apply" @click="apply">
					{{ unchanged && access === 'paid' ? 'Save plans' : `Apply ${ACCESS_LABELS[access]}` }}
				</FormButton>
				<FormButton class="w-full justify-center sm:w-auto" type="secondary" :disabled="busy" @click="close">Cancel</FormButton>
			</footer>
		</template>
	</ModalBox>
</template>
